import { useCallback, useEffect, useMemo, useState } from 'react'
import type { PropsWithChildren } from 'react'
import { ThemeProvider, type ThemeMode } from '@mysaiva/core'
import { safeLocalStorageGet, safeLocalStorageSet } from '@mysaiva/core/utils'
import { ModeContext, THEME_MODE_STORAGE_KEY } from './ModeContext'
import './workspaceTheme.scss'

function readStoredMode(): ThemeMode {
  const stored = safeLocalStorageGet(THEME_MODE_STORAGE_KEY)
  return stored === 'dark' ? 'dark' : 'light'
}

/**
 * Light/dark mode for the Developer Platform workspace (SAIV-11348).
 * The preference is persisted per browser and scoped to the workspace: the
 * product apps stay light regardless of what is chosen here.
 */
export function ModeProvider({ children }: PropsWithChildren) {
  const [mode, setModeState] = useState<ThemeMode>(readStoredMode)

  const setMode = useCallback((next: ThemeMode) => {
    setModeState(next)
    safeLocalStorageSet(THEME_MODE_STORAGE_KEY, next)
  }, [])

  const toggleMode = useCallback(() => {
    setMode(mode === 'dark' ? 'light' : 'dark')
  }, [mode, setMode])

  // Let plain CSS (scss modules) react to the mode without prop drilling.
  useEffect(() => {
    document.documentElement.setAttribute('data-dp-theme', mode)
    return () => document.documentElement.removeAttribute('data-dp-theme')
  }, [mode])

  const value = useMemo(() => ({ mode, setMode, toggleMode }), [mode, setMode, toggleMode])

  return (
    <ModeContext.Provider value={value}>
      <ThemeProvider mode={mode}>{children}</ThemeProvider>
    </ModeContext.Provider>
  )
}
